export enum ListingType {
  SALE = "sale",
  RENT = "rent",
  SHORTLET = "shortlet",
  LEASE = "lease",
}

export enum PropertyCategory {
  RESIDENTIAL = "RESIDENTIAL",
  COMMERCIAL = "COMMERCIAL",
  INDUSTRIAL = "INDUSTRIAL",
  LAND = "LAND",
  SPECIAL_PURPOSE = "SPECIAL_PURPOSE",
}

export const CategoryListingConfig: Record<PropertyCategory, ListingType[]> = {
  [PropertyCategory.RESIDENTIAL]: [
    ListingType.SALE,
    ListingType.RENT,
    ListingType.SHORTLET,
    ListingType.LEASE,
  ],
  [PropertyCategory.COMMERCIAL]: [
    ListingType.SALE,
    ListingType.RENT,
    ListingType.LEASE,
  ],
  [PropertyCategory.INDUSTRIAL]: [
    ListingType.SALE,
    ListingType.RENT,
    ListingType.LEASE,
  ],
  [PropertyCategory.SPECIAL_PURPOSE]: [
    ListingType.SALE,
    ListingType.RENT,
    ListingType.LEASE,
  ],
  [PropertyCategory.LAND]: [ListingType.SALE, ListingType.LEASE],
};

// --- 1. Residential Property Types ---
export enum ResidentialPropertyType {
  HOUSE = "house",
  FLAT_APARTMENT = "flat-and-apartments", // Reconciled from legacy reference
  MINI_FLAT = "mini-flats", // Reconciled from legacy reference
  SELF_CONTAIN = "self-contain", // Reconciled from legacy reference
  TERRACE = "terraces", // Reconciled from legacy reference
  DETACHED_BUNGALOW = "detached-bungalows", // Reconciled from legacy reference
  SEMI_DETACHED_DUPLEX = "semi-detached-duplex", // Reconciled from legacy reference
  FULLY_DETACHED_DUPLEX = "fully-detached-duplex", // Reconciled from legacy reference
  PENTHOUSE = "penthouse",
  MAISONETTE = "maisonette",
  BLOCK_OF_FLATS = "block-of-flats",
}

// --- 2. Commercial Property Types ---
export enum CommercialPropertyType {
  OFFICE_SPACE = "office-spaces", // Reconciled from legacy reference
  CO_WORKING_SPACE = "co-working-space", // Reconciled from legacy reference
  SHOP = "shops", // Reconciled from legacy reference
  SHOP_IN_MALL = "shop-in-a-mall", // Reconciled from legacy reference
  COMMERCIAL_BUILDING = "commercial-properties", // Reconciled from legacy reference
  PLAZA = "plaza",
  RETAIL_SPACE = "retail-space",
  SHOWROOM = "showroom",
  RESTAURANT_CAFE_SPACE = "restaurant-cafe-space",
  HOTEL_GUEST_HOUSE = "hotel-guest-house",
  EVENT_CENTRE = "event-centre",
}

// --- 3. Industrial Property Types ---
export enum IndustrialPropertyType {
  WAREHOUSE = "warehouses", // Reconciled from legacy reference
  FACTORY = "factory",
  WORKSHOP = "workshop",
  COLD_ROOM = "cold-room",
  LOGISTICS_HUB = "logistics-hub",
}

// --- 4. Special Purpose Property Types ---
export enum SpecialPurposePropertyType {
  SCHOOL = "school",
  HOSPITAL = "hospital",
  CHURCH = "church",
  MOSQUE = "mosque",
  FILLING_STATION = "filling-station",
  PETROL_GAS_PLANT = "petrol-gas-plant",
  FARM = "farm",
  GOVERNMENT_PROPERTY = "government-property",
}

// --- 5. Land Property Types ---
export enum LandPropertyType {
  RESIDENTIAL = "residential",
  COMMERCIAL = "commercial",
  INDUSTRIAL = "industrial",
  AGRICULTURAL = "agricultural",
  MIXED_USE = "mixed-use",
}

// Combined Union Type
export type PropertyType =
  | ResidentialPropertyType
  | CommercialPropertyType
  | IndustrialPropertyType
  | SpecialPurposePropertyType
  | LandPropertyType;

// Matrix mapping each category to its property types
export const CategoryPropertyTypeConfig: Record<
  PropertyCategory,
  PropertyType[]
> = {
  [PropertyCategory.RESIDENTIAL]: Object.values(ResidentialPropertyType),
  [PropertyCategory.COMMERCIAL]: Object.values(CommercialPropertyType),
  [PropertyCategory.INDUSTRIAL]: Object.values(IndustrialPropertyType),
  [PropertyCategory.SPECIAL_PURPOSE]: Object.values(SpecialPurposePropertyType),
  [PropertyCategory.LAND]: Object.values(LandPropertyType),
};

export const PropertyTypeLabels: Record<PropertyType, string> = {
  // Residential
  [ResidentialPropertyType.HOUSE]: "House",
  [ResidentialPropertyType.FLAT_APARTMENT]: "Flat / Apartment",
  [ResidentialPropertyType.MINI_FLAT]: "Mini Flat",
  [ResidentialPropertyType.SELF_CONTAIN]: "Self Contain",
  [ResidentialPropertyType.TERRACE]: "Terrace",
  [ResidentialPropertyType.DETACHED_BUNGALOW]: "Detached Bungalow",
  [ResidentialPropertyType.SEMI_DETACHED_DUPLEX]: "Semi-Detached Duplex",
  [ResidentialPropertyType.FULLY_DETACHED_DUPLEX]: "Fully-Detached Duplex",
  [ResidentialPropertyType.PENTHOUSE]: "Penthouse",
  [ResidentialPropertyType.MAISONETTE]: "Maisonette",
  [ResidentialPropertyType.BLOCK_OF_FLATS]: "Block of Flats",

  // Commercial
  [CommercialPropertyType.OFFICE_SPACE]: "Office Space",
  [CommercialPropertyType.CO_WORKING_SPACE]: "Co-working Space",
  [CommercialPropertyType.SHOP]: "Shop",
  [CommercialPropertyType.SHOP_IN_MALL]: "Shop in Mall",
  [CommercialPropertyType.COMMERCIAL_BUILDING]: "Commercial Building",
  [CommercialPropertyType.PLAZA]: "Plaza",
  [CommercialPropertyType.RETAIL_SPACE]: "Retail Space",
  [CommercialPropertyType.SHOWROOM]: "Showroom",
  [CommercialPropertyType.RESTAURANT_CAFE_SPACE]: "Restaurant / Café Space",
  [CommercialPropertyType.HOTEL_GUEST_HOUSE]: "Hotel / Guest House",
  [CommercialPropertyType.EVENT_CENTRE]: "Event Centre",

  // Industrial
  [IndustrialPropertyType.WAREHOUSE]: "Warehouse",
  [IndustrialPropertyType.FACTORY]: "Factory",
  [IndustrialPropertyType.WORKSHOP]: "Workshop",
  [IndustrialPropertyType.COLD_ROOM]: "Cold Room",
  [IndustrialPropertyType.LOGISTICS_HUB]: "Logistics Hub",

  // Special Purpose
  [SpecialPurposePropertyType.SCHOOL]: "School",
  [SpecialPurposePropertyType.HOSPITAL]: "Hospital",
  [SpecialPurposePropertyType.CHURCH]: "Church",
  [SpecialPurposePropertyType.MOSQUE]: "Mosque",
  [SpecialPurposePropertyType.FILLING_STATION]: "Filling Station",
  [SpecialPurposePropertyType.PETROL_GAS_PLANT]: "Petrol / Gas Plant",
  [SpecialPurposePropertyType.FARM]: "Farm",
  [SpecialPurposePropertyType.GOVERNMENT_PROPERTY]: "Government Property",

  // Land
  [LandPropertyType.RESIDENTIAL]: "Residential Plot",
  [LandPropertyType.COMMERCIAL]: "Commercial Plot",
  [LandPropertyType.INDUSTRIAL]: "Industrial Plot",
  [LandPropertyType.AGRICULTURAL]: "Agricultural Land",
  [LandPropertyType.MIXED_USE]: "Mixed-Use Land",
};

export enum PropertyStage {
  OFF_PLAN = "off-plan",
  CARCASS = "carcass",
  SEMI_FINISHED_CARCASS = "semi-finish-carcass",
  FINISHED = "finished",
  NEWLY_BUILT = "newly-built",
  DEVELOPED_LAND = "developed-land",
  SEMI_DEVELOPED_LAND = "semi-developed-land",
  UN_DEVELOPED_LAND = "un-developed-land",
}

// --- Property Currency ---
export enum PropertyCurrencies {
  USD = "USD",
  NGN = "NGN",
  GBP = "GBP",
}

export enum LeaseDurationUnit {
  DAY = "day",
  WEEK = "week",
  MONTH = "month",
  YEAR = "year",
}

// Enums for Land Properties
export enum Topography {
  DRY_LAND = "dry_land",
  SWAMPY = "swampy",
  MARSHY = "marshy",
  SANDY = "sandy",
}

export enum NonLandSalesDocument {
  DEED_OF_ASSIGNMENT = "deed_of_assignment",
  CERTIFICATE_OF_OCCUPANCY = "certificate_of_occupancy",
  GOVERNORS_CONSENT = "governors_consent",
  DEED_OF_CONVEYANCE = "deed_of_conveyance",
  RECEIPT_OF_PURCHASE = "receipt_of_purchase",
  BUILDING_PLAN_APPROVAL = "building_plan_approval",
  POWER_OF_ATTORNEY = "power_of_attorney",
  CONTRACT_OF_SALE = "contract_of_sale",
  DEED_OF_SUBLEASE = "deed_of_sublease",
}
export enum LandTitleDocument {
  C_OF_O = "c_of_o",
  GOVERNOR_CONSENT = "governor_consent",
  GAZETTE = "gazette",
  DEED_OF_ASSIGNMENT = "deed_of_assignment",
  EXCISION = "excision",
  SURVEY_PLAN = "survey_plan",
}

export const LeaseDurationUnitLabels: Record<LeaseDurationUnit, string> = {
  [LeaseDurationUnit.DAY]: "Day(s)",
  [LeaseDurationUnit.WEEK]: "Week(s)",
  [LeaseDurationUnit.MONTH]: "Month(s)",
  [LeaseDurationUnit.YEAR]: "Year(s)",
};

export const LandTitleDocumentLabels: Record<LandTitleDocument, string> = {
  [LandTitleDocument.C_OF_O]: "Certificate of Occupancy (C of O)",
  [LandTitleDocument.GOVERNOR_CONSENT]: "Governor's Consent",
  [LandTitleDocument.GAZETTE]: "Gazette",
  [LandTitleDocument.DEED_OF_ASSIGNMENT]: "Deed of Assignment",
  [LandTitleDocument.EXCISION]: "Excision",
  [LandTitleDocument.SURVEY_PLAN]: "Survey Plan",
};

export const TopographyLabels: Record<Topography, string> = {
  [Topography.DRY_LAND]: "Dry Land",
  [Topography.SWAMPY]: "Swampy",
  [Topography.MARSHY]: "Marshy",
  [Topography.SANDY]: "Sandy",
};

// --- Shared General Amenities ---
export enum GeneralAmenity {
  SECURITY_24_7 = "24_7_security",
  CCTV = "cctv",
  PERIMETER_FENCING = "perimeter_fencing",
  GATE_HOUSE = "gate_house",
  ELECTRICITY = "electricity",
  BACKUP_GENERATOR = "backup_generator",
  SOLAR_POWER = "solar_power",
  WATER_SUPPLY = "water_supply",
  BOREHOLE = "borehole",
  WATER_TREATMENT = "water_treatment",
  DRAINAGE_SYSTEM = "drainage_system",
  PARKING_SPACE = "parking_space",
  TARRED_ROADS = "tarred_roads",
  INTERNET_FIBER = "internet_fiber",
}

// --- Category Specific Amenities Enums ---

export enum ResidentialAmenity {
  SWIMMING_POOL = "swimming_pool",
  GYM = "gym",
  BOYS_QUARTER = "boys_quarter",
  FITTED_KITCHEN = "fitted_kitchen",
  BALCONY = "balcony",
  WALK_IN_CLOSET = "walk_in_closet",
  AIR_CONDITIONING = "air_conditioning",
  INVERTER = "inverter",
  CHILDRENS_PLAY_AREA = "childrens_play_area",
  ELEVATOR = "elevator",
  SMART_HOME_AUTOMATION = "smart_home_automation",
  WATER_HEATER = "water_heater",
  HEAT_EXTRACTOR = "heat_extractor",
}

export enum CommercialAmenity {
  ELEVATOR_LIFT = "elevator_lift",
  CENTRAL_AC = "central_ac",
  RECEPTION_DESK = "reception_desk",
  CONFERENCE_ROOM = "conference_room",
  FIRE_SAFETY_SYSTEM = "fire_safety_system",
  FIBER_OPTIC_READY = "fiber_optic_ready",
  CAFETERIA_CANTEEN = "cafeteria_canteen",
  ACCESSIBLE_RAMPS = "accessible_ramps",
  SIGNAGE_SPACE = "signage_space",
}

export enum IndustrialAmenity {
  HEAVY_DUTY_POWER = "heavy_duty_power",
  OVERHEAD_CRANE = "overhead_crane",
  CONTAINER_ACCESS = "container_access",
  WEIGH_BRIDGE = "weigh_bridge",
  WASTE_MANAGEMENT_PLANT = "waste_management_plant",
  SECURITY_OUTPOST = "security_outpost",
  FIRE_HYDRANT = "fire_hydrant",
}

export enum LandAmenity {
  FENCED_AND_GATED = "fenced_and_gated",
  CORNER_PIECE = "corner_piece",
  BUY_AND_BUILD = "buy_and_build",
  DRY_LAND = "dry_land",
  STREET_LIGHTING = "street_lighting",
}

export enum SpecialPurposeAmenity {
  PUBLIC_ADDRESS_SYSTEM = "public_address_system",
  EMERGENCY_EXITS = "emergency_exits",
  INDUSTRIAL_KITCHEN = "industrial_kitchen",
  AMBULANCE_BAY = "ambulance_bay",
  UNDERGROUND_FUEL_TANKS = "underground_fuel_tanks",
  DISPENSER_PUMPS = "dispenser_pumps",
}

// LEGACY / DEPRECATED: single status enum. The backend has split this into
// PropertyApprovalStatus and PropertyAvailabilityStatus. Keep for backward
// compat only — prefer the two new enums (and the model fields that carry them).
export enum PropertyStatus {
  AVAILABLE = "available",
  UNDER_CONTRACT = "under-contract",
  UNDER_REVIEW = "under-review",
  PENDING = "pending",
  RENTED = "rented",
  LEASED = "leased",
  SOLD = "sold",
  REJECTED = "rejected",
  APPROVED = "approved",
}

export enum PropertyApprovalStatus {
  PENDING = "pending",
  APPROVED = "approved",
  REJECTED = "rejected",
}

export enum PropertyAvailabilityStatus {
  PENDING = "pending",
  AVAILABLE = "available",
  UNDER_REVIEW = "under-review",
  UNDER_CONTRACT = "under-contract",
  RENTED = "rented",
  LEASED = "leased",
  SOLD = "sold",
}

// --- Sub-Interfaces ---

export interface RealtorDetails {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string | null;
  is_partner: boolean;
  plan: string;
  business_name: string;
  join_date: string;
}

export interface PropertyCreatedByDetails {
  id: string;
  full_name: string;
  email: string;
  phone_number: string;
  role: string;
  profile_image_url: string | null;
  state: string | null;
  city: string | null;
}
